import useUser from "../../hooks/useUser";
import {LoadingContent} from "../../components/LoadingContent";
import React, {ReactElement, ReactNode} from "react";
import {Alert, Box, Chip, Divider, Grid, Link, Paper, Tab, Typography, Tooltip} from "@mui/material";
import {TabContext, TabList, TabPanel} from "@mui/lab";
import {Link as RouterLink, useNavigate, useParams, useSearchParams} from "react-router-dom";
import Diversity3Icon from '@mui/icons-material/Diversity3';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import RateReviewIcon from '@mui/icons-material/RateReview';
import useApiCall from "../../hooks/CancellableApiCall";
import {
    GenericIAPanelRankUsersResponse,
    GenericIAPanelResponse,
    GenericIAPanelUserResponse
} from "../../data/internal-affairs/GenericIAPanelResponse";
import {formatTimestamp} from "../../utils";
import {CCMeetingPanel} from "../../components/internal-affairs/CCMeetingPanel";
import {InternalAffairsComplaintMasterList} from "../../components/internal-affairs/InternalAffairsComplaintMasterList";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface InternalAffairsPanelContentProps { data: GenericIAPanelResponse }

function MemberRow({label, members, single, fallback}: {
    label: string
    members: GenericIAPanelUserResponse[] | undefined
    single?: boolean
    fallback?: string
}) {
    if (!members || members.length === 0) {
        return (
            <Box sx={{display: "flex", justifyContent: "space-between", py: 0.5}}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body2" color="text.disabled">{fallback ?? "None"}</Typography>
            </Box>
        )
    }
    if (single) {
        return (
            <Box sx={{display: "flex", justifyContent: "space-between", py: 0.5}}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Link underline="none" component={RouterLink} to={`/user/${members[0].communityId}`} variant="body2" fontWeight={500}>
                    {members[0].nick}
                </Link>
            </Box>
        )
    }
    return (
        <Box sx={{display: "flex", justifyContent: "space-between", py: 0.5, flexWrap: "wrap"}}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Box sx={{display: "flex", gap: 0.5, flexWrap: "wrap"}}>
                {members.map((m, idx) => (
                    <React.Fragment key={m.communityId}>
                        <Link underline="none" component={RouterLink} to={`/user/${m.communityId}`} variant="body2">
                            {m.nick}
                        </Link>
                        {idx < members.length - 1 && <Typography variant="body2">,</Typography>}
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    )
}

function StatRow({label, value}: { label: string; value: number }) {
    return (
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5}}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Chip label={value} size="small" sx={{fontWeight: 600, minWidth: 32}}/>
        </Box>
    )
}

function DashCard({title, action, children}: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
    return (
        <Paper variant="outlined" sx={{p: 2, height: "100%", display: "flex", flexDirection: "column", gap: 1}}>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                <Typography variant="subtitle2" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>{title}</Typography>
                {action}
            </Box>
            <Divider/>
            {children}
        </Paper>
    )
}

const InternalAffairsPanelContent = (props: InternalAffairsPanelContentProps) => {
    interface TabContent {
        title: string
        path?: string
        icon: ReactElement
        content: ReactNode
    }

    const tabs: TabContent[] = []
    const [searchParams, setSearchParams] = useSearchParams()
    const params = useParams()
    const navigate = useNavigate()
    const user = useUser()

    tabs.push({
        title: "Your Complaints",
        path: "assigned",
        icon: <FormatListBulletedIcon/>,
        content: <InternalAffairsComplaintMasterList endpoint={"assigned"} />
    })

    if (user.hasPermissions(["internalAffairs:complaintAdmin"]) || user.hasPermissions(["internalAffairs:viewAllComplaints"])) {
        tabs.push({
            title: "Open Complaints",
            path: "open",
            icon: <FormatListBulletedIcon/>,
            content: <InternalAffairsComplaintMasterList endpoint={"open"} />
        })
    }
    if (user.hasPermissions(["internalAffairs:complaintAdmin"]) || user.hasPermissions(["internalAffairs:viewAllComplaints"]) || user.hasPermissions(["internalAffairs:complaintSeniorReviewBoard"]) || user.hasPermissions(["internalAffairs:complaintJuniorReviewBoard"])) {
        tabs.push({
            title: "Final Reports",
            path: "final-reported",
            icon: <FormatListBulletedIcon/>,
            content: <InternalAffairsComplaintMasterList endpoint={"final-reported"} />
        })
    }
    if (user.hasPermissions(["internalAffairs:complaintSeniorReviewBoard"])) {
        tabs.push({
            title: "Complaint Reviews",
            path: "reviews",
            icon: <RateReviewIcon/>,
            content: <InternalAffairsComplaintMasterList endpoint={"reviews"} />
        })
    }
    if (user.hasPermissions(["internalAffairs:complaintAdmin"]) || user.hasPermissions(["internalAffairs:viewAllComplaints"]) || user.hasPermissions(["internalAffairs:complaintSeniorReviewBoard"]) || user.hasPermissions(["internalAffairs:complaintJuniorReviewBoard"])) {
        tabs.push({
            title: "Complaint Committee Meetings",
            path: "meetings",
            icon: <Diversity3Icon/>,
            content: <CCMeetingPanel />
        })
    }

    const tabIndex = tabs.findIndex((t) => t.path === params.tab) ?? 0

    function changeTab(index: number) {
        const tabValue = tabs[index].path
        const newUrl = `/internal-affairs-dashboard/${tabValue}`
        navigate(newUrl)
    }

    function findRank(rank: string): GenericIAPanelUserResponse[] | undefined {
        const m = props.data.ranksToDisplay.find((r: GenericIAPanelRankUsersResponse) => r.rankIdentifier === rank)?.divisionMembers
        return m?.length ? m : undefined
    }

    const d = props.data
    const stats = d.statistics[0]
    const mStats = d.monthStatistics[0]

    return (
        <Box sx={{mt: 2, display: "flex", flexDirection: "column", gap: 2}}>
            <Grid container spacing={2} alignItems="stretch">

                <Grid item xs={12} sm={6} lg={4}>
                    <DashCard title="Internal Affairs Team">
                        <MemberRow label="Head" members={findRank("internal_affairs_head")} single/>
                        <MemberRow label="Command" members={findRank("internal_affairs_command")} fallback="None"/>
                        <MemberRow label="Jr. Command" members={findRank("internal_affairs_junior_command")} fallback="None"/>
                    </DashCard>
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                    <DashCard title="Complaint Committee">
                        <MemberRow label="Chairman" members={findRank("complaint_committee_chairman")} single/>
                        <MemberRow label="Senior Members" members={findRank("senior_complaint_committee_member")} fallback="None"/>
                        <MemberRow label="Junior Members" members={findRank("junior_complaint_committee_member")} fallback="None"/>
                    </DashCard>
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                    <DashCard title="Line Management">
                        <MemberRow label="Chief" members={findRank("office_of_the_chiefs_chief")} single/>
                        <MemberRow label="Deputy Chief" members={findRank("office_of_the_chiefs_deputy_chief")} fallback="None"/>
                        <MemberRow label="Head of PSD" members={findRank("professional_standards_head")} single/>
                        <MemberRow label="PSD Members" members={findRank("professional_standards_member")} fallback="None"/>
                    </DashCard>
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                    <DashCard title="Complaint Overview">
                        <StatRow label="Under Investigation" value={d.complaintsUnderInvestigation}/>
                        <StatRow label="Pending Classification" value={d.pendingClassification}/>
                        <StatRow label="Meeting Required" value={d.pendingMeeting}/>
                        <StatRow label="Voting Required" value={d.pendingVote}/>
                        <StatRow label="Pending Senior Review" value={d.pendingApproval}/>
                    </DashCard>
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                    <DashCard title="Your Statistics (Last 30 days)" action={
                        <Tooltip title={
                            <Box>
                                <Typography variant="caption" fontWeight={700}>All time</Typography>
                                {([
                                    ["Investigated", stats.investigated],
                                    ["Sustained", stats.sustained],
                                    ["Exonerated", stats.exonerated],
                                    ["Not Sustained", stats.notsustained],
                                    ["Unfounded", stats.unfounded],
                                ] as [string, number][]).map(([l, v]) => (
                                    <Box key={l} sx={{display: "flex", justifyContent: "space-between", gap: 3}}>
                                        <Typography variant="caption">{l}</Typography>
                                        <Typography variant="caption" fontWeight={600}>{v}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        }>
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.5, cursor: "default"}}>
                                <InfoOutlinedIcon sx={{fontSize: 14, color: "text.secondary"}}/>
                                <Typography variant="caption" color="text.secondary">All-time</Typography>
                            </Box>
                        </Tooltip>
                    }>
                        <StatRow label="Investigated" value={mStats.investigated}/>
                        <StatRow label="Sustained" value={mStats.sustained}/>
                        <StatRow label="Exonerated" value={mStats.exonerated}/>
                        <StatRow label="Not Sustained" value={mStats.notsustained}/>
                        <StatRow label="Unfounded" value={mStats.unfounded}/>
                    </DashCard>
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                    <DashCard title="Next Committee Meeting">
                        {d.nextEvent ? (
                            <Box sx={{display: "flex", flexDirection: "column", gap: 0.5}}>
                                <Box sx={{display: "flex", justifyContent: "space-between", py: 0.5}}>
                                    <Typography variant="body2" color="text.secondary">Host</Typography>
                                    <Link underline="none" component={RouterLink} to={`/user/${d.nextEvent.host.communityId}`} variant="body2" fontWeight={500}>{d.nextEvent.host.nick}</Link>
                                </Box>
                                <Box sx={{display: "flex", justifyContent: "space-between", py: 0.5}}>
                                    <Typography variant="body2" color="text.secondary">Time</Typography>
                                    <Typography variant="body2">{formatTimestamp(d.nextEvent.date)}</Typography>
                                </Box>
                                <Divider/>
                                <Typography variant="body2" sx={{pt: 0.5}}>{d.nextEvent.description}</Typography>
                            </Box>
                        ) : (
                            <Alert severity="info">No meetings planned — reach out to Senior CC or the Chairman to schedule one.</Alert>
                        )}
                    </DashCard>
                </Grid>

            </Grid>

            <Paper variant="outlined">
                <TabContext value={tabIndex.toString()}>
                    <Box sx={{borderBottom: 1, borderColor: "divider", px: 1}}>
                        <TabList onChange={(_, newValue) => { setSearchParams(searchParams); changeTab(parseInt(newValue)) }} variant="scrollable" scrollButtons="auto">
                            {tabs.map((t, idx) => <Tab key={t.title} icon={t.icon} iconPosition="start" label={t.title} value={idx.toString()}/>)}
                        </TabList>
                    </Box>
                    {tabs.map((t, idx) =>
                        <TabPanel key={t.title} value={idx.toString()}>
                            {tabIndex === idx ? t.content : null}
                        </TabPanel>
                    )}
                </TabContext>
            </Paper>
        </Box>
    )
}

export const InternalAffairsDashboard = () => {
    const userData = useUser()
    const user = userData.user

    const data = useApiCall<GenericIAPanelResponse>({
        url: `/api/internal-affairs/panel`
    })

    return (
        <LoadingContent isLoading={
            userData.isLoading || !user ||
            data.isLoading || !data.data
        }>
            <InternalAffairsPanelContent data={data.data!!} />
        </LoadingContent>
    )
}
